import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    Icon,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Typography,
} from '@mui/material';
import React, { CSSProperties } from 'react';

import { PatternData } from 'app/model/pattern-data';
import { SeamPattern } from 'app/model/seam-pattern';
import { Strings } from 'app/utils/strings';

interface PatternAccordionProps {
    sequence: number;
    pattern: SeamPattern;
    editable: boolean;
    style?: CSSProperties;

    onEdit?: (data: SeamPattern) => void;
    onDelete?: (data: SeamPattern) => void;
}

export class PatternAccordion extends React.Component<PatternAccordionProps> {
    public render(): React.ReactNode {
        return (
            <Accordion className='mt-1' style={this.props.style}>
                <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'auto auto 150px 1fr auto auto auto',
                            gap: 2,
                            width: '100%',
                            alignItems: 'center',
                        }}
                    >
                        <Icon>grain</Icon>
                        <Chip color='primary' size='small' label={this.props.sequence} />
                        <Typography noWrap>Type: {Strings.patternType(this.props.pattern.type)}</Typography>
                        <Typography
                            sx={{
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere',
                                maxWidth: '100%',
                            }}
                        >
                            {PatternAccordion.computePatternLabel(this.props.pattern).map((s, key) => (
                                <React.Fragment key={key}> &bull; {s}</React.Fragment>
                            ))}
                        </Typography>
                        <Box>{this.props.pattern.outputId ? `Deployed Id: ${this.props.pattern.outputId}` : ''}</Box>
                        <Box>{this.props.pattern.supersedes ? `Supersedes: ${this.props.pattern.supersedes}` : ''}</Box>
                        <Box ml={2}>{(this.props.pattern.tupleCount || 0).toLocaleString()} Tuples</Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <TableContainer>
                        <Table size='small'>
                            <TableBody>
                                <TableRow>
                                    <TableCell colSpan={3} />
                                    <TableCell width='75%' />
                                </TableRow>
                                {this.renderRows()}
                                {this.props.editable ? (
                                    <TableRow>
                                        <TableCell colSpan={4}>
                                            <Button
                                                variant='outlined'
                                                color='primary'
                                                size='small'
                                                onClick={() => this.props.onEdit?.(this.props.pattern)}
                                            >
                                                Edit
                                            </Button>
                                            &nbsp;
                                            <Button
                                                variant='outlined'
                                                color='secondary'
                                                size='small'
                                                onClick={() => this.props.onDelete?.(this.props.pattern)}
                                            >
                                                Delete
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={4}>
                                            <Button
                                                variant='outlined'
                                                color='primary'
                                                size='small'
                                                onClick={() => this.props.onEdit?.(this.props.pattern)}
                                            >
                                                View
                                            </Button>
                                            &nbsp;
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </AccordionDetails>
            </Accordion>
        );
    }

    private renderRows() {
        const elements = [];
        const data = this.props.pattern.data;
        let counter = 1;

        if (data.midPattern) {
            elements.push(
                <TableRow key={counter++}>
                    <TableCell>{counter++}</TableCell>
                    <TableCell>
                        <strong>{Strings.patternDataNames('midPattern').toUpperCase()}</strong>
                    </TableCell>
                    <TableCell>{data.matchOperator.toUpperCase()}</TableCell>
                    <TableCell
                        sx={{
                            whiteSpace: 'normal',
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                            maxWidth: '100%',
                        }}
                    >
                        {data.midPattern}
                    </TableCell>
                </TableRow>,
            );
        }

        if (data.descPattern) {
            elements.push(
                <TableRow key={counter++}>
                    <TableCell>{counter++}</TableCell>
                    <TableCell>
                        <strong>{Strings.patternDataNames('descPattern').toUpperCase()}</strong>
                    </TableCell>
                    <TableCell>{data.matchOperator.toUpperCase()}</TableCell>
                    <TableCell
                        sx={{
                            whiteSpace: 'normal',
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                            maxWidth: '100%',
                        }}
                    >
                        {data.descPattern}
                    </TableCell>
                </TableRow>,
            );
        }

        if (data.mccPattern && 0 < data.mccPattern.length) {
            elements.push(
                <TableRow key={counter++}>
                    <TableCell>{counter++}</TableCell>
                    <TableCell>
                        <strong>{Strings.patternDataNames('mccPattern').toUpperCase()}</strong>
                    </TableCell>
                    <TableCell>{data.mccMatchOperator}</TableCell>
                    <TableCell
                        sx={{
                            whiteSpace: 'normal',
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                            maxWidth: '100%',
                        }}
                    >
                        {data.mccPattern.join(', ')}
                    </TableCell>
                </TableRow>,
            );
        }

        if (data.cityPattern) {
            elements.push(
                PatternAccordion.renderRow(counter++, 'cityPattern', data, data.locationOperator.toUpperCase()),
            );
        }

        if (data.statePattern) {
            elements.push(
                PatternAccordion.renderRow(counter++, 'statePattern', data, data.locationOperator.toUpperCase()),
            );
        }

        if (data.zipPattern) {
            elements.push(
                PatternAccordion.renderRow(counter++, 'zipPattern', data, data.locationOperator.toUpperCase()),
            );
        }

        if (data.countryPattern) {
            elements.push(
                PatternAccordion.renderRow(counter++, 'countryPattern', data, data.locationOperator.toUpperCase()),
            );
        }

        if (data.subChannel) {
            elements.push(PatternAccordion.renderRow(counter++, 'subChannel', data, 'SET TO'));
        }

        if (data.extractField) {
            elements.push(PatternAccordion.renderRow(counter++, 'extractField', data));
        }

        if (data.extractPattern) {
            elements.push(PatternAccordion.renderRow(counter++, 'extractPattern', data, 'REGEX'));
        }

        return elements;
    }

    private static computePatternLabel(pattern: SeamPattern): string[] {
        if (pattern.noReplacement) {
            return ['Deactivates Superseded Pattern'];
        }

        const MAX_PATTERNS = 4;
        const keys = Object.keys(pattern.data) as Array<keyof PatternData>;
        const parts = [];

        for (const field of keys) {
            if ('locationOperator' === field || 'matchOperator' === field || 'mccMatchOperator' === field) {
                continue;
            }

            let value = pattern.data[field];
            if (!value || 0 === value.length) {
                continue;
            }

            if (MAX_PATTERNS === parts.length) {
                parts.push('...');
                break;
            }

            if (Array.isArray(value)) {
                value = value.join(', ');
            }

            parts.push(`${Strings.patternDataNames(field)}: ${value}`);
        }

        return parts;
    }

    private static renderRow(key: number, field: keyof PatternData, data: PatternData, comparison = 'Exact') {
        return (
            <TableRow key={key}>
                <TableCell>{key}</TableCell>
                <TableCell>
                    <strong>{Strings.patternDataNames(field).toUpperCase()}</strong>
                </TableCell>
                <TableCell>{comparison}</TableCell>
                <TableCell>{data[field]}</TableCell>
            </TableRow>
        );
    }
}
